import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaSyncAlt,
    FaArrowUp,
    FaArrowDown,
    FaListUl,
    FaChartLine,
    FaSave,
    FaEdit,
    FaCheckCircle,
    FaTimesCircle
} from "react-icons/fa";

import {
    FaArrowTrendUp,
    FaArrowTrendDown
} from "react-icons/fa6";

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 0) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function moneyFmt(v, digits = 2) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

function sideNorm(side) {
    return String(side || "").toLowerCase();
}

function periodMeta(p) {
    switch (p) {
        case "30s":
            return { title: "30s", sub: "30 second" };
        case "1m":
            return { title: "1m", sub: "1 minute" };
        case "3m":
            return { title: "3m", sub: "3 minute" };
        case "5m":
            return { title: "5m", sub: "5 minute" };
        case "10m":
            return { title: "10m", sub: "10 minute" };
        case "1h":
            return { title: "1h", sub: "1 hour" };
        default:
            return { title: p, sub: "" };
    }
}

export default function LiveTrades() {
    const navigate = useNavigate();
    const { entity_id, period } = useParams();

    // Refs for storing the timeout ID and preventing flicker
    const refreshTimeoutRef = useRef(null);
    const initialLoadDoneRef = useRef(false);
    const isMountedRef = useRef(true);
    const showManualTradeFormRef = useRef(false);

    const PERIODS = useMemo(() => ["30s", "1m", "3m", "5m", "10m", "1h"], []);

    const activePeriod = useMemo(() => {
        const p = String(period || "").trim();
        return PERIODS.includes(p) ? p : "30s";
    }, [period, PERIODS]);

    // Data
    const [entity, setEntity] = useState({ entity_name: "", image: "", period_id: "" });
    const [tradeList, setTradeList] = useState([]);
    const [manualTrade, setManualTrade] = useState({ has_manual_trade: false, side: "" });

    // Manual trade form state
    const [manualTradeSide, setManualTradeSide] = useState("");
    const [isSettingManualTrade, setIsSettingManualTrade] = useState(false);
    const [showManualTradeForm, setShowManualTradeForm] = useState(false);

    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const stats = useMemo(() => {
        let up = 0;
        let down = 0;
        for (const row of tradeList) {
            const s = sideNorm(row?.trade?.side);
            if (s === "up") up += row?.trade?.amount;
            else if (s === "down") down += row?.trade?.amount;
        }

        return { total: up + down, up, down };
    }, [tradeList]);

    const fetchLiveTrades = useCallback(
        async (options = {}) => {
            const { isManualRefresh = false, isAutoRefresh = false } = options;

            if (!entity_id) return;

            // Only set loading states for manual refresh or initial load (not for auto-refresh)
            if (isManualRefresh) {
                setIsRefreshing(true);
            } else if (!isAutoRefresh && !initialLoadDoneRef.current) {
                // Only set loading for initial load (first time)
                setIsLoading(true);
            }

            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/live-trades`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token") || "",
                        period: activePeriod,
                        entity_id: entity_id,
                    },
                    timeout: 15000,
                });

                const out = res?.data;

                if (out?.error) {
                    // Only show toast for non-auto-refresh errors
                    if (!isAutoRefresh) {
                        toast.error(typeof out?.error === "string" ? out.error : "Failed to load live trades");
                    }
                    return;
                }

                const payload = out?.data || {};
                const nextList = Array.isArray(payload?.trade_list) ? payload.trade_list : [];
                const nextEntity = payload?.entity || { entity_name: "", image: "", period_id: "" };
                const manualTradeData = out?.manual_trade || { has_manual_trade: false, side: "" };

                setTradeList(nextList);
                setEntity(nextEntity);
                setManualTrade(manualTradeData);

                // Only update manualTradeSide if form is NOT open
                // This prevents auto-refresh from resetting user's selection
                if (!showManualTradeFormRef.current) {
                    if (manualTradeData.has_manual_trade && manualTradeData.side) {
                        setManualTradeSide(manualTradeData.side);
                    } else {
                        setManualTradeSide("");
                    }
                }

                if (isManualRefresh) {
                    toast.success("Refreshed");
                }

                // Mark initial load as done after first successful fetch
                if (!initialLoadDoneRef.current && !isAutoRefresh) {
                    initialLoadDoneRef.current = true;
                }
            } catch (error) {
                if (!isAutoRefresh) {
                    if (error?.code === "ECONNABORTED") {
                        toast.error("Request timeout");
                    } else {
                        toast.error("Failed to load live trades");
                    }
                }
            } finally {
                // Only clear loading states if not auto-refresh
                if (!isAutoRefresh) {
                    setIsLoading(false);
                    setIsRefreshing(false);
                }
            }
        },
        [entity_id, activePeriod]
    );

    // Update ref when showManualTradeForm changes
    useEffect(() => {
        showManualTradeFormRef.current = showManualTradeForm;
    }, [showManualTradeForm]);

    // Function to set manual trade
    const setManualTradeResult = useCallback(async () => {
        if (!entity_id || !manualTradeSide) {
            toast.error("Please select a side");
            return;
        }

        setIsSettingManualTrade(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/set-manual-trade`,
                headers: { "Content-Type": "application/json" },
                data: {
                    token: localStorage.getItem("token") || "",
                    entity_id: entity_id,
                    period: activePeriod,
                    side: manualTradeSide
                },
                timeout: 15000,
            });

            const data = res?.data;

            if (data?.error) {
                toast.error(typeof data?.error === "string" ? data.error : "Failed to set manual trade");
                return;
            }

            toast.success("Manual trade result set successfully");

            // Update local state
            setManualTrade({
                has_manual_trade: true,
                side: manualTradeSide
            });

            // Update the form side to match what we just set
            setManualTradeSide(manualTradeSide);

            // Hide form after successful set
            setShowManualTradeForm(false);

            // Refresh data to get updated state
            fetchLiveTrades({ isManualRefresh: false });

        } catch (error) {
            if (error?.code === "ECONNABORTED") {
                toast.error("Request timeout");
            } else {
                toast.error("Failed to set manual trade");
            }
        } finally {
            setIsSettingManualTrade(false);
        }
    }, [entity_id, activePeriod, manualTradeSide, fetchLiveTrades]);

    // Setup auto-refresh every 3 seconds (no loading states)
    const setupAutoRefresh = useCallback(() => {
        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        // Set new timeout for 3 seconds
        refreshTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && entity_id) {
                fetchLiveTrades({ isAutoRefresh: true });
                setupAutoRefresh(); // Setup next refresh
            }
        }, 3000);
    }, [entity_id, fetchLiveTrades]);

    // Initial fetch when entity_id or period changes
    useEffect(() => {
        if (!entity_id) {
            setIsLoading(false);
            toast.error("entity_id missing in URL");
            return;
        }

        // Reset initial load flag for new entity/period
        initialLoadDoneRef.current = false;

        // Reset data for new entity/period
        setEntity({ entity_name: "", image: "", period_id: "" });
        setTradeList([]);
        setManualTrade({ has_manual_trade: false, side: "" });
        setManualTradeSide("");
        setShowManualTradeForm(false);

        // Fetch initial data (will show loading)
        fetchLiveTrades({});

        // Setup auto refresh (will not show loading)
        setupAutoRefresh();

        // Cleanup function to clear timeout on unmount or dependency change
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, [entity_id, activePeriod, fetchLiveTrades, setupAutoRefresh]);

    // Clear timeout when component unmounts
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }
        };
    }, []);

    const onTab = (p) => {
        if (!entity_id) return;
        navigate(`/live-trades/${encodeURIComponent(entity_id)}/${encodeURIComponent(p)}`, { replace: true });
    };

    const onManualRefresh = () => {
        fetchLiveTrades({ isManualRefresh: true });
    };

    // Handle form opening - set the current manual trade side
    const handleOpenForm = () => {
        setShowManualTradeForm(true);
        // Set form side to current manual trade side if exists
        if (manualTrade.has_manual_trade && manualTrade.side) {
            setManualTradeSide(manualTrade.side);
        } else {
            setManualTradeSide("");
        }
    };

    // Handle form cancel - reset to current state
    const handleCancelForm = () => {
        setShowManualTradeForm(false);
        // Reset to current manual trade state
        if (manualTrade.has_manual_trade && manualTrade.side) {
            setManualTradeSide(manualTrade.side);
        } else {
            setManualTradeSide("");
        }
    };

    const coinName =
        safeText(entity?.entity_name) !== "—" && safeText(entity?.entity_name) !== ""
            ? safeText(entity?.entity_name)
            : safeText(entity_id);

    return (
        <PageTransition>
            <style>{`
        .btn-compact { padding: 4px 8px !important; line-height: 1.1 !important; }
        .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
        .coin-img { width: 44px; height: 44px; border-radius: 12px; object-fit: cover; }
        .lt-table th, .lt-table td { padding: 6px 6px !important; vertical-align: middle; }
        .lt-table th:not(:last-child), .lt-table td:not(:last-child) { border-right: 1px solid rgba(0,0,0,0.06); }

        .tab-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
        @media (max-width: 991.98px) { .tab-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 575.98px) { .tab-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }

        .period-tab {
          border: 1px solid rgba(0,0,0,0.12);
          background: #fff;
          border-radius: 14px;
          padding: 12px 12px;
          text-align: left;
          width: 100%;
          transition: transform .10s ease, border-color .10s ease, background .10s ease;
        }
        .period-tab:hover { transform: translateY(-1px); border-color: rgba(13,110,253,0.35); background: rgba(13,110,253,0.05); }
        .period-tab.active { border-color: rgba(13,110,253,0.65); background: rgba(13,110,253,0.08); }
        .period-title { font-weight: 800; font-size: 1rem; line-height: 1.1; }
        .period-sub { margin-top: 4px; font-size: 0.78rem; color: rgba(0,0,0,0.55); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .stat-card { border-radius: 14px; padding: 10px 12px; color: white; }
        .stat-total { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .stat-up { background: linear-gradient(135deg, #0bab64 0%, #3bb78f 100%); }
        .stat-down { background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%); }
        .stat-label { font-size: 0.78rem; color: rgba(255,255,255,0.85); display: flex; align-items: center; gap: 6px; }
        .stat-value { font-size: 1.05rem; font-weight: 800; margin-top: 2px; }
        
        .manual-trade-card { background: #f8f9fa; border-left: 4px solid #0d6efd; }
        .manual-trade-set { background: #f0f9ff; border-left: 4px solid #0ab64f; }
        .side-up { color: #0ab64f; }
        .side-down { color: #dc3545; }
        .manual-trade-badge { font-size: 0.8rem; padding: 4px 10px; }
      `}</style>

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-3">
                    {entity?.image ? (
                        <img className="coin-img" src={entity.image} alt={coinName} />
                    ) : (
                        <div
                            className="coin-img d-flex align-items-center justify-content-center"
                            style={{ background: "rgba(13,110,253,0.12)", color: "#0d6efd" }}
                        >
                            <FaListUl />
                        </div>
                    )}

                    <div>
                        <h2 className="fw-bold mb-0">Live Trades</h2>
                        <div className="text-muted">
                            <span className="fw-semibold">{coinName}</span> • Period:{" "}
                            <span className="fw-semibold">{activePeriod}</span>
                            {entity?.period_id ? (
                                <>
                                    {" "}
                                    • Period ID: <span className="fw-semibold mono">{safeText(entity?.period_id)}</span>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="d-flex gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-compact d-inline-flex align-items-center gap-2"
                        onClick={onManualRefresh}
                        disabled={isRefreshing || isLoading}
                        title="Refresh"
                    >
                        <FaSyncAlt className={isRefreshing ? "fa-spin" : ""} />
                        {isRefreshing ? "Refreshing..." : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Manual Trade Result Section */}
            <div className="card mb-3 border-0 shadow-sm">
                <div className={`card-body ${manualTrade.has_manual_trade ? 'manual-trade-set' : 'manual-trade-card'}`}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div>
                                <h5 className="fw-bold mb-1">Manual Trade Result</h5>
                                <div className="text-muted small">
                                    Set the final result for this trading period
                                </div>
                            </div>

                            {manualTrade.has_manual_trade ? (
                                <div className="d-flex align-items-center gap-2">
                                    <span className={`badge manual-trade-badge ${manualTrade.side === 'up' ? 'text-bg-success' : 'text-bg-danger'}`}>
                                        {manualTrade.side === 'up' ? (
                                            <>
                                                <FaCheckCircle className="me-1" /> Up
                                            </>
                                        ) : (
                                            <>
                                                <FaTimesCircle className="me-1" /> Down
                                            </>
                                        )}
                                    </span>
                                    <span className="text-muted small">
                                        Result is set
                                    </span>
                                </div>
                            ) : (
                                <span className="badge manual-trade-badge text-bg-warning">
                                    <FaEdit className="me-1" /> Not Set
                                </span>
                            )}
                        </div>

                        <div className="d-flex gap-2">
                            {!showManualTradeForm ? (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                                    onClick={handleOpenForm}
                                >
                                    <FaEdit /> {manualTrade.has_manual_trade ? "Change Result" : "Set Result"}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleCancelForm}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Manual Trade Form */}
                    {showManualTradeForm && (
                        <div className="mt-3 pt-3 border-top">
                            <div className="row g-3 align-items-end">
                                <div className="col-12 col-md-6 col-lg-4">
                                    <label className="form-label fw-semibold">Select Side</label>
                                    <div className="d-flex gap-2">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="manualTradeSide"
                                                id="sideUp"
                                                value="up"
                                                checked={manualTradeSide === "up"}
                                                onChange={(e) => setManualTradeSide(e.target.value)}
                                            />
                                            <label className="form-check-label d-flex align-items-center gap-2 fw-semibold side-up" htmlFor="sideUp">
                                                <FaArrowUp /> Up
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="manualTradeSide"
                                                id="sideDown"
                                                value="down"
                                                checked={manualTradeSide === "down"}
                                                onChange={(e) => setManualTradeSide(e.target.value)}
                                            />
                                            <label className="form-check-label d-flex align-items-center gap-2 fw-semibold side-down" htmlFor="sideDown">
                                                <FaArrowDown /> Down
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6 col-lg-4">
                                    <div className="text-muted small">
                                        Current: {entity_id} • {activePeriod}
                                    </div>
                                </div>

                                <div className="col-12 col-md-12 col-lg-4 d-flex justify-content-lg-end">
                                    <button
                                        type="button"
                                        className="btn btn-success d-inline-flex align-items-center gap-2"
                                        onClick={setManualTradeResult}
                                        disabled={!manualTradeSide || isSettingManualTrade}
                                    >
                                        <FaSave className={isSettingManualTrade ? "fa-spin" : ""} />
                                        {isSettingManualTrade ? "Setting..." : "Save Result"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Period Tabs */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="tab-grid">
                        {PERIODS.map((p) => {
                            const meta = periodMeta(p);
                            return (
                                <button
                                    type="button"
                                    key={p}
                                    className={`period-tab ${activePeriod === p ? "active" : ""}`}
                                    onClick={() => onTab(p)}
                                >
                                    <div className="period-title">{meta.title}</div>
                                    <div className="period-sub">{meta.sub}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content - Show loading only on initial load */}
            {isLoading ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-muted">Loading live trades…</div>
                </div>
            ) : (
                <>
                    {/* Stats with colored cards and icons */}
                    <div className="row g-2 mb-3">
                        <div className="col-12 col-md-4">
                            <div className="stat-card stat-total">
                                <div className="stat-label">
                                    <FaChartLine /> Total Coin
                                </div>
                                <div className="stat-value">{nfmt(stats.total, 0)}</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="stat-card stat-up">
                                <div className="stat-label">
                                    <FaArrowTrendUp /> Up Coin
                                </div>
                                <div className="stat-value">{nfmt(stats.up, 0)}</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="stat-card stat-down">
                                <div className="stat-label">
                                    <FaArrowTrendDown /> Down Coin
                                </div>
                                <div className="stat-value">{nfmt(stats.down, 0)}</div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="p-2 table-responsive">
                                <table className="table table-sm align-middle mb-0 lt-table">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 90 }}>Side</th>
                                            <th style={{ width: 100 }} className="text-end">
                                                Amount
                                            </th>
                                            <th style={{ width: 140 }}>User ID</th>
                                            <th style={{ width: 220 }}>Name</th>
                                            <th style={{ width: 220 }}>Mobile</th>
                                            <th style={{ width: 260 }}>Email</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {tradeList.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center text-muted py-5">
                                                    <div className="fw-semibold">No live trades</div>
                                                    <div className="small">Click the Refresh button to update the data.</div>
                                                </td>
                                            </tr>
                                        ) : (
                                            tradeList.map((row, idx) => {
                                                const u = row?.user || {};
                                                const t = row?.trade || {};
                                                const s = sideNorm(t?.side);

                                                const mobileDisplay =
                                                    safeText(u?.mobile) === "—"
                                                        ? "—"
                                                        : `+${safeText(u?.country_code) === "—" ? "" : safeText(u?.country_code)} ${safeText(
                                                            u?.mobile
                                                        )}`.trim();

                                                return (
                                                    <tr key={`${safeText(u?.user_id)}-${safeText(t?.amount)}-${idx}`}>
                                                        <td>
                                                            {s === "up" ? (
                                                                <span className="badge text-bg-success d-inline-flex align-items-center gap-1">
                                                                    <FaArrowUp /> Up
                                                                </span>
                                                            ) : s === "down" ? (
                                                                <span className="badge text-bg-danger d-inline-flex align-items-center gap-1">
                                                                    <FaArrowDown /> Down
                                                                </span>
                                                            ) : (
                                                                <span className="badge text-bg-secondary">—</span>
                                                            )}
                                                        </td>

                                                        <td className="text-end fw-semibold">{moneyFmt(t?.amount, 2)}</td>

                                                        <td className="mono fw-semibold">{safeText(u?.user_id)}</td>

                                                        <td className="fw-semibold">{safeText(u?.name)}</td>

                                                        <td className="fw-semibold">{mobileDisplay}</td>

                                                        <td className="fw-semibold" style={{ wordBreak: "break-word" }}>
                                                            {safeText(u?.email)}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </PageTransition>
    );
}